import { modalUtils } from '@/app/core/utils';
import {
  getInputFileFromRemote,
  getInputFileHeadersFromRemote,
  sendMail,
  getLayout,
} from '@/app/features/products/common/services';
import { FieldMappings } from '@/app/features/products/common/components/frontend-processing/models';
import { useState, useEffect } from 'react';
import { Table } from 'antd';
import dayjs, { extend as dayJSExtend } from 'dayjs';
import utcPlugin from 'dayjs/plugin/utc';
import {
  HeaderInfo,
  InputRemoteFile,
  InputRemoteFileVersion,
  FileLayoutData,
  isLayoutRequired,
  InputLabelsMap,
} from '@ssar-app/domain';
import { bytesToSize } from '@ssar-app/uikit';
import { Flex, Text, VStack } from '@chakra-ui/react';

dayJSExtend(utcPlugin);
interface FileVersionsListProps {
  record: InputRemoteFile;
  beforeUpload: (
    headersInfo: HeaderInfo,
    file?: File,
    content?: string[],
    experianFileName?: string,
    clientFileName?: string,
    totalRecords?: number,
    intentType?: string,
    fileId?: string,
  ) => void;
  onSuccess: (
    clientFileName: string,
    serverFileName: string,
    fieldMappings: FieldMappings[],
    hasHeaders?: boolean,
    recordsExpected?: number,
  ) => void;
  inputLabelsMap?: InputLabelsMap;
  setIsModalVisible: (arg0: boolean) => void;
  productName?: string;
}

export const FileVersionsList = (props: FileVersionsListProps) => {
  const { record } = props;
  const [versionsData, setVersionData] = useState<InputRemoteFileVersion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);

  const columns = [
    {
      title: 'File Id',
      dataIndex: 'fileId',
      sorter: true,
      render: (text: string, row: InputRemoteFileVersion) => (
        <SelectButton row={row}></SelectButton>
      ),
    },
    {
      title: 'File Size',
      dataIndex: 'fileSizeMb',
      render: (size: number) => bytesToSize(size),
      sorter: (a: InputRemoteFileVersion, b: InputRemoteFileVersion) =>
        (b?.fileSizeMb || 0) - (a?.fileSizeMb || 0),
    },
    {
      title: 'Total Records',
      dataIndex: 'totalRecords',
      sorter: (a: InputRemoteFileVersion, b: InputRemoteFileVersion) =>
        (b?.totalRecords || 0) - (a?.totalRecords || 0),
    },
    {
      title: 'LandingZone Uploaded Time',
      dataIndex: 'landingZoneFileUploadedTime',
      render: (text: number) => {
        return dayjs(text).format('YYYY-MMM-DD HH:mm:ss [GMT]ZZ');
      },
      sorter: (a: InputRemoteFileVersion, b: InputRemoteFileVersion) =>
        dayjs(b.timeUploaded).diff(dayjs(a.timeUploaded)),
    },
    {
      title: 'Uploaded Date',
      dataIndex: 'timeUploaded',
      render: (text: number) => {
        return dayjs(text).format('YYYY-MMM-DD HH:mm:ss [GMT]ZZ');
      },
      sorter: (a: InputRemoteFileVersion, b: InputRemoteFileVersion) =>
        dayjs(b.timeUploaded).diff(dayjs(a.timeUploaded)),
    },
    {
      title: 'Has Headers',
      dataIndex: 'hasHeaders',
      sorter: true,
      render: (val: boolean) => (val ? 'True' : 'False'),
    },
  ];

  interface SelectButtonProps {
    row: InputRemoteFileVersion;
  }
  const getFieldMappings = (LayoutData: FileLayoutData): FieldMappings[] => {
    return LayoutData.layout.reduce((acc, field, index) => {
      const value = !field?.experianHeader ? undefined : field?.experianHeader;
      const effectiveHeader = value || field.clientHeader;
      const recordIndex = acc.findIndex(
        (a) => a.field === (field.experianHeader || index.toString()),
      );
      const toInsert: FieldMappings = {
        field: field.clientHeader || index.toString(),
        headerNameWithLength: {
          name: value,
          fieldLength: props.inputLabelsMap
            ? props.inputLabelsMap[effectiveHeader.toLowerCase()]
                ?.fieldLength || 50
            : 50,
        },
      };
      if (recordIndex === -1) {
        acc.push(toInsert);
      } else {
        acc[recordIndex] = toInsert;
      }
      return acc;
    }, [] as FieldMappings[]);
  };
  const SelectButton = (buttonProps: SelectButtonProps) => {
    const selectClicked = (): void => {
      setIsLoading(true);
      if (props.record.errors && props.record.errors.length !== 0) {
        const errorDetails: Record<string, string> = {
          ...props.record.errors[0],
          fileName: props.record.fileName,
          fileType: 'Independently Uploaded',
        };
        sendMail('ML-Failure', errorDetails).then((_res) => {
          // console.log(res);
        });
      }
      Promise.all([
        getInputFileFromRemote(buttonProps.row.fileId, 100, true),
        getInputFileHeadersFromRemote(buttonProps.row.fileId),
      ])

        .then(([contentResponse, headersResponse]) => {
          props.beforeUpload(
            headersResponse.data?.headersInfo as HeaderInfo,
            undefined,
            contentResponse?.data || [],
            props.record.fileName,
            props.record.fileName,
            buttonProps.row.totalRecords,
            props.record.intentType,
            buttonProps.row.fileId,
          );
        })
        .catch((error) => {
          modalUtils.showError(error, 'Error while getting input file.');
        })
        .finally(() => {
          setIsLoading(false);
        });
    };
    const isLayoutAvailable = (): void => {
      setIsLoading(true);
      getLayout(buttonProps.row.fileId)
        .then((FileLayoutData) => {
          if (FileLayoutData.status !== 'Success') {
            selectClicked();
          } else {
            props.onSuccess(
              props.record.fileName,
              props.record.fileName,
              getFieldMappings(FileLayoutData.data as FileLayoutData),
              buttonProps.row.hasHeaders as boolean,
              buttonProps.row.totalRecords,
            );
            props.setIsModalVisible(false);
            // console.log(FileLayoutData.data);
          }
        })
        .catch((error) => {
          if (error.message === 'Could not get File Layout') {
            selectClicked();
          } else {
            setIsLoading(false);
            modalUtils.showError(error, 'Error while getting Layout.');
          }
        });
    };

    return (
      <a
        onClick={(e) => {
          e.preventDefault();
          if (isLayoutRequired.getLayout) {
            isLayoutAvailable();
          } else {
            selectClicked();
          }
        }}
        href='/'
      >
        {buttonProps.row.fileId}
      </a>
    );
  };

  useEffect(() => {
    setVersionData(record.versions);
  }, [record.versions]);

  return (
    <>
      {versionsData ? (
        <Flex background={'gray.100'} p={4}>
          <VStack w={'full'}>
            <Text pb={2} fontSize={'md'} w={'full'}>
              File Versions
            </Text>
            <Table
              style={{ width: '100%' }}
              loading={isLoading}
              columns={columns}
              dataSource={versionsData}
              // scroll={{ scrollToFirstRowOnChange: true, y: 300 }}
              pagination={{
                current: pageNo,
                onChange: (page) => setPageNo(page),
                total: versionsData.length,
                showTotal: (total, range) =>
                  `Showing ${range[0]}-${range[1]} of ${total} items`,
                position: ['bottomLeft'],
                showSizeChanger: true,
                simple: false,
              }}
            ></Table>
          </VStack>
        </Flex>
      ) : (
        <div>No data/permission.</div>
      )}
    </>
  );
};
